import { inject, Injectable, OnDestroy } from '@angular/core';
import { environment } from '@env/environment';
import { supportedNotices } from '@models/constants';
import { DestroyableComponent } from '@models/destroyable.component';
import { WebEvent } from 'lingo2-api-models';
import {
  AnyType,
  BillingOperation,
  EntityWatchType,
  GenericNotice,
  IAccountUpdate,
  IBalanceUpdate,
  IChatMessageUpdate,
  IChatThreadUpdate,
  IEntityUpdate,
  INoticeEvent,
  INoticeOptions,
  IPagedResults,
  IPlanUpdate,
  IProfileVerificationNotice,
  IReferenceUpdate,
} from 'lingo2-models';
import { BehaviorSubject, Observable, Observer, of, shareReplay, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, share, takeUntil, tap } from 'rxjs/operators';
import { io } from 'socket.io-client';
import { ContextService } from '../services';
import { AccessTokenService } from '../services/access-token.service';
import { IWebsocketService, IWsMessage } from './websocket.interfaces';

/*
 */
export interface INoticeRead {
  unread_count: number;
}

export interface INoticeOptionsUpdate {
  result: 'done';
}

export interface INoticeEventsPagedResults extends IPagedResults<INoticeEvent[]> {
  unread_count: number;
}

export interface IWsAuthEvent {
  success: boolean;
  user: { id: string };
}

export interface IWsPongEvent {
  user: { id: string };
}

/*
 */
const SUPPORTED_WEB_EVENTS = [
  WebEvent.AuthComplete,
  WebEvent.SystemAction,
  WebEvent.NoticeRead,
  WebEvent.Notice,
  WebEvent.Notices,
  WebEvent.NoticeOptions,
  WebEvent.NoticeOptionsUpdate,
  WebEvent.ChatMessage,
  WebEvent.ChatThread,
  WebEvent.Reference,
  WebEvent.Verification,
  WebEvent.Account,
  WebEvent.Balance,
  WebEvent.Plan,
  WebEvent.Update,
];

/*
 */
@Injectable({
  providedIn: 'root',
})
export class WebsocketService extends DestroyableComponent implements IWebsocketService, OnDestroy {
  private token_srv = inject(AccessTokenService);
  private contextService = inject(ContextService);

  private readonly _TAG = '### WebsocketService';

  private socket = null;
  private ws_opts: any = {}; // todo, Interface Socket.io opts

  public status$: Observable<boolean>;
  private _authorized = new BehaviorSubject<boolean>(false);
  public authorized$ = this._authorized.asObservable();

  private connection$: Observer<boolean>;
  private receiveMessages$: Subject<IWsMessage<AnyType>> = this.register(new Subject<IWsMessage<AnyType>>());

  private readonly subscriptions = new Subscription();
  private subscriptionsInitialized = false;
  private readonly eventStreams = new Map<string, Subject<IWsMessage<AnyType>>>();

  private isConnected = false;
  private _authUserId: string;
  private watches = new Map<string, { entity: EntityWatchType; id: string }>();
  private networkError = false;

  /*
   */
  constructor() {
    super();

    if (!this.isBrowser) {
      return;
    }

    console.debug(`${this._TAG}: ws_url=`, environment.ws_url);
    this.initMessageTypes();
    this.setupConnectionStatus();
    this.setupUserSubscription();
    this.initializeWebSocket();
  }

  /*
   */
  private ws_auth_payload = () => ({ jwt: this.token_srv.getAccessToken() });
  private set_net_status = (status: boolean, log: string, ...err: any) => {
    this.connection$.next(status);
    this.networkError = !status;
    console.debug(`${this._TAG}: ${log}`, err);
  };

  /*
   */
  private initMessageTypes(): void {
    SUPPORTED_WEB_EVENTS.forEach((e) => this.eventStreams.set(e, this.register(new Subject<IWsMessage<AnyType>>())));
  }

  /*
   */
  private initializeWebSocket(): void {
    if (this._authUserId) {
      this.setupSubscriptions();
      this.auth();
      this.connect();
    }
  }

  /*
   */
  private setupConnectionStatus(): void {
    this.status$ = new Observable<boolean>((observer) => (this.connection$ = observer)).pipe(
      share(),
      distinctUntilChanged(),
    );
  }

  /*
   */
  private setupUserSubscription(): void {
    this.subscriptions.add(
      this.contextService.me$
        .pipe(
          filter((user) => (user?.id ?? null) !== this._authUserId),
          debounceTime(1000),
          takeUntil(this.destroyed$),
        )
        .subscribe({
          next: (user) => {
            this._authUserId = user?.id ?? null;
            this.setupSubscriptions();
            this.auth();
            this.connect();
          },
          error: (error) => {
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          },
        }),
    );
  }

  /*
   */
  private setupSubscriptions(): void {
    if (this.subscriptionsInitialized) {
      return;
    }
    this.subscriptionsInitialized = true;

    this.subscriptions.add(
      this.status$.subscribe({
        next: (isConnected) => {
          this.isConnected = isConnected;
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      }),
    );

    this.subscriptions.add(
      this.onAuthComplete$()
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (event) => {
            this._authorized.next(event.success);
            if (event.success) {
              this.restartWatchingAll();
            }
          },
          error: (error) => {
            this.errorNotificationService.captureError(error, 'SOCKET-ERROR');
          },
        }),
    );
  }

  /*
   */
  public ngOnDestroy(): void {
    this.stopWatchingAll();
    this.disconnect();
    this.subscriptions.unsubscribe();
    super.ngOnDestroy();
  }

  /*
   */
  private auth() {
    this.send(WebEvent.Auth, {
      token: this.ws_auth_payload()?.jwt || null,
      features: {
        notices: supportedNotices,
      },
    });
  }

  /*
   */
  private initSocketEvents(): void {
    const events = [
      ['connect', () => this.set_net_status(true, 'Connected')],
      ['reconnect', (a: any) => this.set_net_status(true, 'Reconnect: ', a)],
      ['reconnect_attempt', (a: any) => this.set_net_status(false, 'Reconnecting...', a)],
      ['reconnect_error', (e: any) => this.set_net_status(false, 'Reconnection error:', e)],
      ['reconnect_failed', (e: any) => this.set_net_status(false, 'Reconnection failed:', e)],
      ['disconnect', (a: any) => this.set_net_status(false, 'Disconnected: ', a)],
    ] as const;

    events.forEach(([event, handler]) => this.socket.on(event, handler));

    this.socket.on('connect_error', (err: any) => {
      if (!this.socket.active) {
        this.set_net_status(false, 'Connection error!', err);
        this.errorNotificationService.captureError(err, 'SOCKET-ERROR');
      }
    });

    this.socket.onAny((e: any, pl: any) => {
      const event = pl?.event || e;
      const data = pl?.data ? pl.data : pl;
      this.add_message({ event, data });
      // this.receiveMessages$.next({ event: pl?.event || e, data: pl?.data ? pl.data : pl });
    });
  }

  /*
   */
  private add_message(msg: IWsMessage<AnyType>) {
    if (!this.eventStreams.has(msg.event)) {
      return;
    }
    this.eventStreams.get(msg.event).next({ event: msg.event, data: msg.data });
  }

  /*
   */
  private connect(): void {
    if (this.socket || !this._authUserId) {
      return;
    }

    this.ws_opts = {
      reconnectionDelayMax: 1000,
      auth: {
        token: this.ws_auth_payload()?.jwt || null,
        features: {
          notices: supportedNotices,
        },
      },
    };

    this.connection$.next(false);
    this.socket = io(environment.ws_url, this.ws_opts);
    this.initSocketEvents();
    this.socket.connect();
  }

  /*
   */
  private disconnect(): void {
    this.socket?.disconnect();
    this.connection$?.complete();
  }

  /*
   */
  public on<T>(event: string): Observable<T> {
    if (!event || !this.eventStreams.get(event)) {
      return of();
    }

    return this.eventStreams
      .get(event)
      .asObservable()
      .pipe(
        map((message: IWsMessage<T>) => message.data),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        }),
        takeUntil(this.destroyed$),
      );

    // if (!this.eventStreams.has(event)) {
    //   const stream$ = this.receiveMessages$.pipe(
    //     tap((msg) => console.log(`@@@@`, msg)),
    //     filter(
    //       (message: IWsMessage<T>) => typeof message === 'object' && 'event' in message && message.event === event,
    //     ),
    //     map((message: IWsMessage<T>) => message.data),
    //     // shareReplay({
    //     //   bufferSize: 1,
    //     //   refCount: true,
    //     // }),
    //     takeUntil(this.destroyed$),
    //   );

    //   this.eventStreams.set(event, stream$);
    // }
  }

  /*
   */
  public send(event: string, data: AnyType = {}): void {
    if (!event || !this.isConnected || !this.platform.isBrowser) {
      return;
    }
    this.socket.emit(event, data);
  }

  /** Подключить наблюдение за изменениями сущности */
  public startWatching(entity: EntityWatchType, id: string) {
    if (!this.platform.isBrowser || !id) {
      return;
    }
    this.send(WebEvent.WatchStart, { entity, id });
    this.watches.set(`${entity}-${id}`, { entity, id });
  }

  /** Отключить наблюдение за изменениями сущности */
  public stopWatching(entity: EntityWatchType, id: string) {
    if (!this.platform.isBrowser || !id) {
      return;
    }
    this.send(WebEvent.WatchStop, { entity, id });
    this.watches.delete(`${entity}-${id}`);
  }

  /** Отключить наблюдение за любыми изменениями сущностей */
  public stopWatchingAll() {
    if (!this.platform.isBrowser) {
      return;
    }
    this.send(WebEvent.WatchStopAll);
    this.watches.clear();
  }

  /** Добавить в наблюдаемые всех ранее наблюдаемых пользователей */
  protected restartWatchingAll() {
    this.watches.forEach((data) => {
      const { entity, id } = data;
      this.startWatching(entity, id);
    });
  }

  /* Events
   *
   */
  /** Сообщение об авторизации на сокет-сервере */
  public onAuthComplete$(): Observable<IWsAuthEvent> {
    return this.on<IWsAuthEvent>(WebEvent.AuthComplete);
  }

  /** Сообщение о системном событии */
  public onSystemAction$(): Observable<GenericNotice<AnyType>> {
    return this.on<GenericNotice<AnyType>>(WebEvent.SystemAction);
  }

  /** Сообщение о количестве непрочитанных уведомлений */
  public onNoticeRead$(): Observable<INoticeRead> {
    return this.on<INoticeRead>(WebEvent.NoticeRead);
  }

  /** Новое уведомление */
  public onNotice$(): Observable<INoticeEvent> {
    return this.on<INoticeEvent>(WebEvent.Notice);
  }

  /** Страница уведомлений */
  public onNotices$(): Observable<INoticeEventsPagedResults> {
    return this.on<INoticeEventsPagedResults>(WebEvent.Notices);
  }

  /** Настройки уведомлений */
  public onNoticeOptions$(): Observable<INoticeOptions> {
    return this.on<INoticeOptions>(WebEvent.NoticeOptions);
  }

  /** Подтверждение обновления настроек уведомлений */
  public onNoticeOptionsUpdate$(): Observable<INoticeOptionsUpdate> {
    return this.on<INoticeOptionsUpdate>(WebEvent.NoticeOptionsUpdate);
  }

  /** Изменение сообщения в нити чата */
  public onChatMessage$(): Observable<IChatMessageUpdate> {
    return this.on<IChatMessageUpdate>(WebEvent.ChatMessage);
  }

  /** Изменение нити чата */
  public onChatThread$(): Observable<IChatThreadUpdate> {
    return this.on<IChatThreadUpdate>(WebEvent.ChatThread);
  }

  /** Изменение справочников */
  public onReferenceUpdate$(): Observable<IReferenceUpdate> {
    return this.on<IReferenceUpdate>(WebEvent.Reference);
  }

  /** Верификация профиля пользователя */
  public onVerification$(): Observable<IProfileVerificationNotice> {
    return this.on<IProfileVerificationNotice>(WebEvent.Verification);
  }

  /** Изменение учётной записи */
  public onAccountUpdate$(): Observable<IAccountUpdate> {
    return this.on<IAccountUpdate>(WebEvent.Account).pipe(debounceTime(1000));
  }

  /** Изменение финансового профиля (баланс) */
  public onBalanceUpdate$(): Observable<IBalanceUpdate> {
    return this.on<IBalanceUpdate>(WebEvent.Balance).pipe(debounceTime(1000));
  }

  /** Изменение финансового профиля (подписки) */
  public onPlanUpdate$(): Observable<IPlanUpdate> {
    return this.on<IPlanUpdate>(WebEvent.Plan).pipe(debounceTime(1000));
  }
  /** Изменение операции */
  public onBillingOperation(id: string): Observable<BillingOperation> {
    return of(null);
  }

  /** Изменение сущности */
  public onEntityUpdate(entity: EntityWatchType, id: string): Observable<IEntityUpdate> {
    return this.on<IEntityUpdate>(WebEvent.Update).pipe(
      filter((update) => update.entity === entity && update.id === id),
      debounceTime(1000),
    );
  }
}
